/**
 * Shared wizard context and hook.
 *
 * Both OnboardingWizardProvider (client-side, localStorage-driven) and
 * ServerDrivenWizardProvider (server-driven) provide this exact context shape,
 * so all step components can work unchanged with either provider.
 */

import { createContext, useContext } from "react";
import type {
  OnboardingConfig,
  StepConfig,
  Achievement,
} from "../../data/onboardingConfig.types";
import type {
  WizardActionExecutor,
  WizardActionContext,
  WizardActionResult,
} from "../../hooks/useOnboardingActions";

export interface OnboardingWizardContextType {
  // State
  currentStep: number;
  stepConfig: StepConfig;
  formData: Record<string, string>;
  completedSteps: boolean[];
  isFirstStep: boolean;
  isLastStep: boolean;
  totalSteps: number;
  // Navigation
  goNext: () => void;
  goBack: () => void;
  updateFormData: (data: Record<string, string>) => void;
  markStepComplete: (index: number) => void;
  // Actions
  executeAction: WizardActionExecutor;
  // Gamification
  totalPoints: number;
  maxPoints: number;
  unlockedAchievements: string[];
  pendingCelebrations: Array<{ achievementId: string; points: number }>;
  onStepComplete: (stepIndex: number) => void;
  consumeCelebration: () => void;
  getAchievement: (id: string) => Achievement | undefined;
  achievements: Achievement[];
  // Config
  config: OnboardingConfig;
}

export const OnboardingWizardContext = createContext<
  OnboardingWizardContextType | undefined
>(undefined);

export function useOnboardingWizard(): OnboardingWizardContextType {
  const ctx = useContext(OnboardingWizardContext);
  if (!ctx)
    throw new Error(
      "useOnboardingWizard must be used within OnboardingWizardProvider or ServerDrivenWizardProvider"
    );
  return ctx;
}

// Re-export types for convenience
export type { WizardActionExecutor, WizardActionContext, WizardActionResult };
